import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import Alert from "../../../Common/Alert";
import DatatableFunc from "../../../Common/DatatableFunc";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import ReactDataTable from "../../../Common/ReactDataTable";

const List = () => {
  const location = useLocation();
  const [receiveData, setReceiveData] = useState([]);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);

    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [selectedShape, setSelectedShape] = useState(null);
    const [statusValue, setStatusValue] = useState("");
    const [isUpdating, setIsUpdating] = useState(false);
    const [updateAlert, setUpdateAlert] = useState(null);

  // Fetch company data from the server
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await Http.get(`/companies`);
      setReceiveData(res.data || []);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    DatatableFunc();
    fetchData();
  }, []);

  const showAlert = (message, type) => {
    setAlert({ message: message, type: type });
    setTimeout(() => {
      setAlert(null);
    }, 4000);
  };

    const handleStatusSubmit = async (e) => {
      e.preventDefault();
      if (!selectedShape) return;
  
      setIsUpdating(true);
      try {
        const response = await Http.put(`/companies-status/${selectedShape.id}`, {
          status: statusValue,
        });
  
        if (response.status === 200) {
          setUpdateAlert({
            type: "success",
            message: "Status updated successfully!",
          });
  
          // Update the local data
          setReceiveData((prevData) =>
            prevData.map((item) =>
              item.id === selectedShape.id
                ? { ...item, status: statusValue }
                : item
            )
          );
  
          // Close modal after a short delay
          setTimeout(() => {
            handleModalClose();
          }, 1500);
        }
      } catch (error) {
        console.error("Update error:", error);
        setUpdateAlert({
          type: "error",
          message: error.response?.data?.message || "Failed to update status",
        });
      } finally {
        setIsUpdating(false);
      }
    };

  // Handle modal open
  const handleStatusUpdate = (rowData) => {
    setSelectedShape(rowData);
    setStatusValue(rowData.status || "");
    setShowModal(true);
    setUpdateAlert(null);
  };

  // Handle modal close
  const handleModalClose = () => {
    setShowModal(false);
    setSelectedShape(null);
    setStatusValue("");
    setUpdateAlert(null);
  };

  useEffect(() => {
    if (!location.state) return;
    showAlert(location.state.message, location.state.type);
  }, [location]);

  // Delete item with confirmation dialog
  const deleteData = (id, deleteApiUrl, showMsg) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        Http.delete("/" + deleteApiUrl + "/" + id).then((res) => {
          fetchData();
          Swal.fire("Deleted!", showMsg + " has been deleted.", "success");
        });
      }
    });
  };

  function formatStatusText(text) {
    if (!text) return "";

    // Replace underscore with space
    let formatted = text.replace(/_/g, " ");

    // Make first word uppercase, keep the rest lowercase
    let parts = formatted.split(" ");

    return parts.join(" ");
  }


  const cols = [
    { field: "code", header: "CODE" },
    { field: "name", header: "NAME" },
    { field: "company_type", header: "TYPE" },
    {
      field: (rowData) => {
        return formatStatusText(formatStatusText(rowData.status));
      },
      header: " Status ",
    },
  ];

  return (
    <div>
      <IsLogin />
      <div>
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <h5 className="am-title">All Companies</h5>
            <Link to="/create-company" className="btn btn-primary">
              <i className="fa fa-plus-square mg-r-6"></i>New
            </Link>
          </div>

          <div className="am-pagebody">
            <Alert alert={alert} />

            <div className="card pd-20 pd-sm-40">
              <ReactDataTable
                cols={cols}
                receiveData={receiveData}
                isLoading={loading}
                actionTemplate={(item) => actionTemplate(item, deleteData, handleStatusUpdate)}
                deleteData={deleteData}
              />
            </div>
              {/* Status Update Modal */}
          {showModal && (
            <div
              className="modal fade show"
              style={{
                display: "block",
                backgroundColor: "rgba(0,0,0,0.5)",
              }}
            >
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Update Status</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={handleModalClose}
                      disabled={isUpdating}
                    ></button>
                  </div>
                  <form onSubmit={handleStatusSubmit}>
                    <div className="modal-body">
                      {updateAlert && (
                        <div
                          className={`alert alert-${
                            updateAlert.type === "success"
                              ? "success"
                              : "danger"
                          }`}
                        >
                          {updateAlert.message}
                        </div>
                      )}
                      <div className="mb-3">
                        <label htmlFor="statusInput" className="form-label">
                          Status
                        </label>
                        <select
                          id="statusInput"
                          className="form-select"
                          value={statusValue}
                          onChange={(e) => setStatusValue(e.target.value)}
                          required
                          disabled={isUpdating}
                        >
                          <option value="">Select Status</option>
                          <option value="active">Active</option>
                        </select>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleModalClose}
                        disabled={isUpdating}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isUpdating || !statusValue}
                      >
                        {isUpdating ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Updating...
                          </>
                        ) : (
                          "Update Status"
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Action buttons for each row
const actionTemplate = (item, deleteData, handleStatusUpdate) => {
  return (
    <td className="text-xs">
      {/* <Link
        to={{
          pathname: "/edit-company/" + item.id,
        }}
        className="btn btn-primary btn-sm ml-1"
      >
        <i className="fa fa-pencil" aria-hidden="true"></i>
      </Link>
      <Link
        to={{
          pathname: "/company-details/" + item.id,
        }}
        className="btn btn-success btn-sm ms-1"
        title="Distributor Details"
      >
        <i className="fa fa-user"></i>
      </Link> */}

      {/* {item.status == "on_test" && (
        <button
          className="btn btn-info btn-sm me-1"
          onClick={() => handleStatusUpdate(item)}
          title="Update Status"
        >
          <i className="fa fa-edit"></i>
        </button>
      )} */}

      <button
        className="btn btn-danger btn-sm ms-1"
        onClick={() => deleteData(item.id, "companies", "companies")}
      >
        <i className="fa fa-trash"></i>
      </button>
    </td>
  );
};

export default List;
